import { LiveEvent } from '@/pages/LiveEvent/store/liveEvent/types';
import { useLearningByIdQueryRemote } from '@/store/v2';
import { formatTimeDependingOnUserTimezone } from '@/utils/dates';
import { getIsLiveEventActive, getIsLiveEventEnded } from '@/pages/LiveEvent/store/utils';
import { useCurrentUser } from '@/store/currentUser';
import { useUserContentLastParticipations } from '@/store/v2/catalog-v2/use-user-last-participations';

type UsePracticalAssessmentViewerProps = {
  id: string;
};

type UsePracticalAssessmentViewerHook = {
  loading: boolean;
  liveEvent: LiveEvent | null;
};

export const useLiveEventViewer = ({ id }: UsePracticalAssessmentViewerProps): UsePracticalAssessmentViewerHook => {
  const { user } = useCurrentUser();
  const { data, loading } = useLearningByIdQueryRemote({
    variables: { id: String(id), includeProgress: true, includeLocations: true },
  });

  const liveEventData = data?.learning;

  const contentURN = liveEventData?.urnV2 || `urn:collegial:self:learning:${id}`;

  const { participations } = useUserContentLastParticipations({
    userId: user?.id,
    contentURNs: [contentURN],
  });

  const participation = participations?.find((p) => p.objectId === contentURN);

  const locationsWithDerived =
    (liveEventData?.locations || []).map((loc) => {
      const hasStart = !!loc?.startDate;
      const hasEnd = !!loc?.endDate;

      const startDate = !loading && hasStart ? new Date(loc.startDate as Date) : undefined;
      const endDate = !loading && hasEnd ? new Date(loc.endDate as Date) : undefined;

      const isStartingSoon =
        startDate && endDate
          ? getIsLiveEventActive({
              startDate,
              endDate,
            })
          : false;

      const hasEnded = endDate ? getIsLiveEventEnded({ endDate }) : false;

      const dateAndTime =
        !loading && hasStart && hasEnd
          ? formatTimeDependingOnUserTimezone({
              startTime: loc?.startDate as Date,
              endTime: loc?.endDate as Date,
              formatOptions: {
                showTimezone: true,
              },
            })
          : '';

      const dateWithoutWeekDay =
        !loading && hasStart && hasEnd
          ? formatTimeDependingOnUserTimezone({
              startTime: loc?.startDate as Date,
              endTime: loc?.endDate as Date,
              formatOptions: {
                showTimezone: true,
                startFormat: 'dd MMM, HH:mm',
              },
            })
          : '';

      const monthName = startDate ? startDate.toLocaleString('en-US', { month: 'short' }) : '';
      const dayOfMonth = startDate ? startDate.getDate().toString() : '';

      return {
        ...loc,
        dateAndTime,
        dateWithoutWeekDay,
        monthName,
        dayOfMonth,
        isStartingSoon,
        hasEnded,
      };
    }) || [];

  return {
    loading,
    liveEvent: {
      id: liveEventData?.id,
      spaceId: liveEventData?.spaceId,
      title: liveEventData?.title || '',
      teaser: liveEventData?.teaser || '',
      image: liveEventData?.image || '',
      language: liveEventData?.languageId || 0,
      meta: liveEventData?.meta,
      locationId: liveEventData?.locations?.[0]?.id,
      timezone: liveEventData?.meta?.timezone,
      progress: liveEventData?.progress
        ? {
            completed: liveEventData.progress.completed,
            completedPercentage: liveEventData.progress.completedPercentage,
          }
        : undefined,
      completedAt: participation?.completedAt || undefined,
      locations: locationsWithDerived,
    } as LiveEvent,
  };
};
